// src/encounter/care-pathway-timeline.tsx
//
// The care pathway as one section of the encounter's right panel (Antonin, 15 Oct: "right panel of
// the encounter is unusable. make it clean collapsible sections"). ONE renderer for every host:
// the pre-encounter panel, the started encounter's panel and the frame-v2 Timeline tab. Before
// this there were three (the DS CarePathwayRail, a DS ActivityTimelineRow list in the drawer, and
// the frame-v2 tab's own lines), and they disagreed on the header, the rows and the empty lines.
//
// FLUTTER SOURCE (spec, read not edited) — `timeline_tab.dart`: the "Care pathway, Today" header
// with the Hide/Show-completed and Collapse/Expand toggles (:158-198, both plain view toggles), row
// anatomy (:201-288: title = `name`, muted sub-line = `displayName`, a type glyph, completed check
// vs pending clock, a day marker on the first row of each day), the hide-completed filter (:88-92).
// Here: Collapse is the section's own header toggle, Hide completed is a header icon action, the
// day ("Today") is the header meta beside the count.
//
// THE FOUR OUTCOMES STAY FOUR (review #314): still loading, the read failed (with Retry), no feed
// wired (`activities` undefined), a pathway with nothing on it (`[]`). Reading any of the other three
// as "this patient has no care-pathway activity" is a clinical misread, so each keeps its own line.
// This component fetches nothing: the hosts own the read (`CarePathwayFeed` in care-pathway-rail.tsx,
// `WorkspaceTimeline`), and it never falls back to a fixture.
import { useState } from 'react';
import { Activity, ClipboardList, EyeOff, FlaskConical, Pill, Stethoscope, type LucideIcon } from 'lucide-react';
import { Button, type ActivityKind } from '@scripsteam/scrips-signal-ds';
import { GlyphText } from '@/shared/ui/components/status-glyph';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { PanelSection, PanelSectionAction } from './panel-section';
import type { CarePathwayActivity } from './lib/care-pathway';

/** The row glyph by activity kind: the DS ActivityTimelineRow's own Lucide set, drawn neutral. */
const KIND_ICON: Record<ActivityKind, LucideIcon> = {
  lab: FlaskConical,
  medication: Pill,
  appointment: Stethoscope,
  measurement: Activity,
  lifestyle: Activity,
  task: ClipboardList,
};

export interface CarePathwayTimelineProps {
  /** The feed. `undefined` = no feed wired; `[]` = read, and empty. NEVER defaulted to a fixture. */
  activities?: CarePathwayActivity[];
  /** The read is in flight. */
  loading?: boolean;
  /** The read failed. With `onRetry`, a Retry button. */
  error?: boolean;
  onRetry?: () => void;
  /** The day beside the title. Flutter literal: "Care pathway, Today" (timeline_tab.dart:171). */
  dayLabel?: string;
}

export function CarePathwayTimeline({ activities, loading = false, error = false, onRetry, dayLabel = 'Today' }: CarePathwayTimelineProps) {
  const [hideCompleted, setHideCompleted] = useState(false);
  const all = activities ?? [];
  const rows = hideCompleted ? all.filter((a) => !a.completed) : all;
  const settled = !loading && !error;
  const anyCompleted = all.some((a) => a.completed);

  // Meta: the day, then the count once the feed has answered (never a "0" while it is still loading).
  const meta = settled && activities !== undefined ? `${dayLabel} · ${all.length}` : dayLabel;

  let body;
  if (loading) {
    body = <QuietLoading label="Loading care pathway…" />;
  } else if (error) {
    body = (
      <p data-slot="panel-section-line">
        Care pathway couldn’t be loaded.
        {onRetry ? (
          <Button variant="link" size="sm" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </p>
    );
  } else if (activities === undefined) {
    body = <p data-slot="panel-section-line">Care pathway isn’t available yet.</p>;
  } else if (all.length === 0) {
    body = <p data-slot="panel-section-line">No care-pathway activity for this appointment yet.</p>;
  } else if (rows.length === 0) {
    // Every row filtered out by Hide completed: a different state from "no activity".
    body = <p data-slot="panel-section-line">All activities are complete.</p>;
  } else {
    body = (
      <ol data-slot="care-pathway-rows">
        {rows.map((a) => {
          const Glyph = KIND_ICON[a.kind ?? 'task'];
          return (
            <li key={a.id} data-slot="care-pathway-row">
              {a.date ? <span data-slot="care-pathway-day">{a.date}</span> : null}
              <span data-slot="care-pathway-item">
                <Glyph aria-hidden data-slot="care-pathway-glyph" />
                <span data-slot="care-pathway-text">
                  <span data-slot="care-pathway-name">{a.name}</span>
                  {a.detail ? <span data-slot="care-pathway-detail">{a.detail}</span> : null}
                </span>
                {a.completed ? <GlyphText kind="done">Completed</GlyphText> : <GlyphText kind="empty">Pending</GlyphText>}
              </span>
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <PanelSection
      id="care-pathway"
      title="Care pathway"
      meta={meta}
      // Open while there is something to see or to act on (rows, a load in flight, a failure to retry);
      // closed when the feed answered with nothing.
      defaultOpen={loading || error || all.length > 0}
      actions={
        // Only when it can change something: with no completed activity there is nothing to hide.
        anyCompleted ? (
          // One name, its state in `aria-pressed` (a toggle that renames itself reads as two buttons).
          <PanelSectionAction
            label="Hide completed"
            icon={<EyeOff />}
            pressed={hideCompleted}
            onClick={() => setHideCompleted((v) => !v)}
          />
        ) : undefined
      }
    >
      {body}
    </PanelSection>
  );
}
